import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isValidGeoCoord, NEARBY_SHOP_RADIUS_KM } from "../utils/geo";
import {
  requestCurrentPosition,
  resolveRegisteredLocation,
  resolveRegisteredLocationCoords,
} from "../utils/registeredLocation";

const LocationPreferenceContext = createContext(null);

const HIDDEN_ROUTE_PREFIXES = [
  "/login",
  "/login/partner",
  "/signup",
  "/forgot-password",
  "/onboarding/",
  "/admin/",
  "/vendor/",
  "/mechanics/",
  "/garage/",
  "/shipping/",
  "/customer/",
];

function isStorefrontPath(pathname = "") {
  return !HIDDEN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix)
  );
}

export function LocationPreferenceProvider({ children }) {
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [registeredLocation, setRegisteredLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      setLoading(true);
      setError("");
      setSearchLocation(null);
      setShowPrompt(false);

      const [{ coords: current, error: currentError }, registered] = await Promise.all([
        requestCurrentPosition(),
        isAuthenticated ? resolveRegisteredLocation(user) : Promise.resolve(null),
      ]);

      if (cancelled) return;

      const validCurrent = isValidGeoCoord(current?.lat, current?.lng) ? current : null;
      const validRegistered = registered
        ? await resolveRegisteredLocationCoords(registered)
        : null;

      setCurrentLocation(validCurrent);
      setRegisteredLocation(validRegistered || registered || null);

      if (currentError) {
        setError(currentError);
      }

      if (validRegistered && validCurrent) {
        setShowPrompt(true);
        setLoading(false);
        return;
      }

      if (validCurrent) {
        setSearchLocation(validCurrent);
      } else if (validRegistered) {
        setSearchLocation(validRegistered);
      }

      setLoading(false);
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const chooseRegisteredLocation = useCallback(async () => {
    const resolved = await resolveRegisteredLocationCoords(registeredLocation);
    if (!isValidGeoCoord(resolved?.lat, resolved?.lng)) {
      setError("Registered address has no valid coordinates. Add a pincode or enable GPS.");
      setShowPrompt(false);
      return;
    }
    setSearchLocation(resolved);
    setRegisteredLocation(resolved);
    setShowPrompt(false);
    setError("");
  }, [registeredLocation]);

  const chooseCurrentLocation = useCallback(() => {
    if (!isValidGeoCoord(currentLocation?.lat, currentLocation?.lng)) {
      setError("Enable location access in your browser to use current location.");
      return;
    }
    setSearchLocation(currentLocation);
    setShowPrompt(false);
    setError("");
  }, [currentLocation]);

  const dismissPrompt = useCallback(() => {
    setShowPrompt(false);
    setError("");
  }, []);

  const reopenPrompt = useCallback(() => {
    if (
      isValidGeoCoord(registeredLocation?.lat, registeredLocation?.lng) &&
      isValidGeoCoord(currentLocation?.lat, currentLocation?.lng)
    ) {
      setShowPrompt(true);
    }
  }, [registeredLocation, currentLocation]);

  const isStorefront = isStorefrontPath(pathname);
  const shouldShowPrompt = showPrompt && isStorefront;

  const value = useMemo(
    () => ({
      loading,
      error,
      registeredLocation,
      currentLocation,
      searchLocation,
      showPrompt: shouldShowPrompt,
      radiusKm: NEARBY_SHOP_RADIUS_KM,
      chooseRegisteredLocation,
      chooseCurrentLocation,
      dismissPrompt,
      reopenPrompt,
      isReady: !loading && (!!searchLocation || !showPrompt),
      searchLocationLabel: searchLocation?.label || "",
    }),
    [
      loading,
      error,
      registeredLocation,
      currentLocation,
      searchLocation,
      shouldShowPrompt,
      chooseRegisteredLocation,
      chooseCurrentLocation,
      dismissPrompt,
      reopenPrompt,
    ]
  );

  return (
    <LocationPreferenceContext.Provider value={value}>
      {children}
    </LocationPreferenceContext.Provider>
  );
}

export function useLocationPreference() {
  const context = useContext(LocationPreferenceContext);
  if (!context) {
    throw new Error("useLocationPreference must be used within LocationPreferenceProvider");
  }
  return context;
}
